import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { AuthService } from '../core/auth.service';
import { CartService } from '../core/cart.service';
import { ChatService } from '../core/chat.service';
import { ToastService } from '../core/toast.service';
import { ChatFormatPipe } from './chat-format.pipe';
import { ProductCardComponent } from './product-card.component';
import { ChatMessage } from '../core/chat.service';

@Component({
  selector: 'app-chat-widget',
  imports: [FormsModule, TranslatePipe, ProductCardComponent, ChatFormatPipe],
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.css',
})
export class ChatWidgetComponent {
  protected readonly chat = inject(ChatService);
  private readonly auth = inject(AuthService);
  private readonly cart = inject(CartService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  protected readonly draft = signal('');
  private readonly messagesEl = viewChild<ElementRef<HTMLElement>>('messagesEl');
  private readonly cardComponents = viewChildren(ProductCardComponent);
  private readonly cardElements = viewChildren(ProductCardComponent, { read: ElementRef });
  private readonly textEls = viewChildren<ElementRef<HTMLElement>>('textEl');
  protected readonly wrappedMessageIndexes = signal<ReadonlySet<number>>(new Set());
  private historyRequested = false;
  /** null until the first effect tick establishes a baseline, so app bootstrap never fires a toast. */
  private previousCartCount: number | null = null;
  /** Set right before a loadOlder() prepend so the auto-scroll-to-bottom effect below skips that
   * one messages() change - without this, every "load older" page would immediately scroll back
   * down to the newest message instead of staying put where the user was reading. */
  private suppressAutoScrollOnce = false;

  constructor() {
    // Clicking a product card (or "add to cart" on a multi-variant product, which redirects to
    // its product page) navigates the app underneath -- without this, the panel is `position:
    // fixed` on top of everything, so the navigation silently "does nothing" from the shopper's
    // point of view.
    inject(Router)
      .events.pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => this.chat.close());

    // Keep the latest turn in view -- without this the panel always opens/updates scrolled to the
    // oldest message instead of the newest. Runs on every message change and on open/close so
    // reopening the widget also lands at the bottom. setTimeout(0) waits for the @for block to
    // actually render before measuring scrollHeight.
    effect(() => {
      this.chat.messages();
      this.chat.open();
      if (this.suppressAutoScrollOnce) {
        this.suppressAutoScrollOnce = false;
        return;
      }
      setTimeout(() => {
        const el = this.messagesEl()?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    });

    effect(() => {
      this.chat.messages();
      setTimeout(() => {
        const wrapped = new Set<number>();
        const range = document.createRange();
        this.textEls().forEach((el, i) => {
          range.selectNodeContents(el.nativeElement);
          if (range.getClientRects().length > 1) {
            wrapped.add(i);
          }
        });
        this.wrappedMessageIndexes.set(wrapped);
      });
    });

    // The cart badge lives in the top-right header -- easy to miss while looking at the chat
    // panel in the opposite corner. Add-to-cart itself shows no toast anywhere in the app (it's
    // silent sitewide), so surface one here specifically while the panel is open.
    effect(() => {
      const count = this.cart.count();
      if (this.previousCartCount !== null && count > this.previousCartCount && this.chat.open()) {
        this.toast.show(this.translate.instant('chat.addedToCart'));
      }
      this.previousCartCount = count;
    });
  }

  protected scrollCards(rail: HTMLElement, direction: -1 | 1): void {
    rail.scrollBy({ left: direction * rail.clientWidth, behavior: 'smooth' });
    // An open-but-undecided variant picker on the card being scrolled away from would otherwise
    // keep the whole message bubble tall, leaving dead space under whichever card is now showing.
    const components = this.cardComponents();
    const elements = this.cardElements();
    components.forEach((card, i) => {
      if (rail.contains(elements[i]?.nativeElement ?? null)) {
        card.closeVariantPicker();
      }
    });
  }

  /** Fires on every scroll inside the panel - cheap to no-op repeatedly, so no debounce needed.
   * Near-the-top (not just exactly 0, since momentum/trackpad scrolling can overshoot past 0)
   * triggers loading the next older page. */
  protected onMessagesScroll(el: HTMLElement): void {
    if (el.scrollTop > 80 || !this.chat.hasMoreHistory() || this.chat.loadingOlder()) {
      return;
    }
    const heightBeforePrepend = el.scrollHeight;
    this.suppressAutoScrollOnce = true;
    this.chat.loadOlder().subscribe(() => {
      setTimeout(() => {
        // Keeps whatever the user was reading in the same visual spot instead of jumping -
        // the prepended older turns push everything down by exactly this height difference.
        el.scrollTop += el.scrollHeight - heightBeforePrepend;
      });
    });
  }

  protected toggle(): void {
    this.chat.toggle();
    if (this.chat.open() && !this.historyRequested) {
      this.historyRequested = true;
      // Guests reopening the widget must see a blank slate (RBA-5774 AC3) -- only logged-in
      // customers get their history reloaded.
      if (this.auth.isAuthenticated()) {
        this.chat.loadHistory();
      }
    }
  }

  protected isWideMessage(message: ChatMessage, index: number): boolean {
    return (message.products?.length ?? 0) > 0 || this.wrappedMessageIndexes().has(index);
  }

  protected submit(event: Event): void {
    event.preventDefault();
    const text = this.draft();
    this.draft.set('');
    this.chat.send(text);
  }
}
